/**
 * Booking forecast.
 *
 * Projects the next N arrival months from the last 12 months of arrivals.
 * Method (deliberately simple and explainable to staff):
 *   1. Bucket non-cancelled requests by arrival month.
 *   2. Use the same calendar month one year earlier as the seasonal baseline.
 *   3. Scale it by a year-over-year trend factor (last 12 months vs the 12
 *      before that), clamped so a thin history cannot produce wild numbers.
 *   4. When there is no same-month history, fall back to the 12-month average.
 *
 * Also reports how many requests are ALREADY on the books for each future
 * month, so staff can compare pipeline against the projection.
 */

const DAY = 86_400_000;

export type ForecastInputRow = {
  status: string;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
};

export type ForecastMonth = {
  key: string;
  label: string;
  /** Requests already received for this future month. */
  onBooks: number;
  /** Projected total requests for the month. */
  predictedRequests: number;
  /** Projected approved stays. */
  predictedApproved: number;
  /** Projected guest nights. */
  predictedNights: number;
  /** Same month last year, for context. */
  lastYearRequests: number;
  basis: "last-year" | "average";
};

export type BookingForecast = {
  months: ForecastMonth[];
  /** Year-over-year multiplier applied to the seasonal baseline. */
  trend: number;
  /** Months of history that actually contain arrivals. */
  historyMonths: number;
  confidence: "low" | "medium" | "high";
  totalPredicted: number;
  totalOnBooks: number;
  max: number;
};

function monthKey(d: Date) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });
}

function isCancelled(status: string) {
  const s = (status ?? "").toLowerCase();
  return s === "cancelled" || s === "canceled";
}

function nightsOf(row: ForecastInputRow) {
  if (!row.arrival_date) return 1;
  const start = new Date(`${row.arrival_date}T00:00:00Z`).getTime();
  const end = row.departure_date
    ? new Date(`${row.departure_date}T00:00:00Z`).getTime()
    : start + DAY;
  return Math.max(1, Math.round((end - start) / DAY));
}

export function buildBookingForecast(
  rows: ForecastInputRow[],
  horizonMonths = 6,
  now: Date = new Date(),
): BookingForecast {
  type Bucket = { requests: number; approved: number; nights: number };
  const hist = new Map<string, Bucket>();
  const bucket = (key: string) => {
    let b = hist.get(key);
    if (!b) hist.set(key, (b = { requests: 0, approved: 0, nights: 0 }));
    return b;
  };

  for (const row of rows) {
    if (!row.arrival_date || isCancelled(row.status)) continue;
    const b = bucket(row.arrival_date.slice(0, 7));
    b.requests += 1;
    if ((row.status ?? "").toLowerCase() === "approved") {
      b.approved += 1;
      b.nights += nightsOf(row);
    }
  }

  const thisMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const keyAt = (offset: number) =>
    monthKey(new Date(Date.UTC(thisMonth.getUTCFullYear(), thisMonth.getUTCMonth() + offset, 1)));

  // Trailing 12 completed-ish months (the current month included) vs the 12 before.
  let recent = 0;
  let prior = 0;
  let approved12 = 0;
  let nights12 = 0;
  let historyMonths = 0;
  for (let i = 0; i < 12; i++) {
    const r = hist.get(keyAt(-i));
    const p = hist.get(keyAt(-i - 12));
    if (r) {
      recent += r.requests;
      approved12 += r.approved;
      nights12 += r.nights;
      if (r.requests > 0) historyMonths += 1;
    }
    if (p) prior += p.requests;
  }

  const trend = prior > 0 ? Math.min(2, Math.max(0.5, recent / prior)) : 1;
  const avgPerMonth = recent / 12;
  const approvalRate = recent > 0 ? approved12 / recent : 0.6;
  const nightsPerApproved = approved12 > 0 ? nights12 / approved12 : 3;

  const months: ForecastMonth[] = [];
  for (let i = 1; i <= horizonMonths; i++) {
    const d = new Date(Date.UTC(thisMonth.getUTCFullYear(), thisMonth.getUTCMonth() + i, 1));
    const key = monthKey(d);
    const lastYear = hist.get(keyAt(i - 12));
    const onBooks = hist.get(key)?.requests ?? 0;

    const basis: ForecastMonth["basis"] = lastYear && lastYear.requests > 0 ? "last-year" : "average";
    const baseline = basis === "last-year" ? (lastYear as Bucket).requests * trend : avgPerMonth;
    // Never predict fewer requests than are already booked for that month.
    const predictedRequests = Math.max(onBooks, Math.round(baseline));
    const predictedApproved = Math.round(predictedRequests * approvalRate);
    const predictedNights = Math.round(predictedApproved * nightsPerApproved);

    months.push({
      key,
      label: monthLabel(d),
      onBooks,
      predictedRequests,
      predictedApproved,
      predictedNights,
      lastYearRequests: lastYear?.requests ?? 0,
      basis,
    });
  }

  const confidence: BookingForecast["confidence"] =
    historyMonths >= 9 && recent >= 24 ? "high" : historyMonths >= 4 && recent >= 8 ? "medium" : "low";

  return {
    months,
    trend,
    historyMonths,
    confidence,
    totalPredicted: months.reduce((s, m) => s + m.predictedRequests, 0),
    totalOnBooks: months.reduce((s, m) => s + m.onBooks, 0),
    max: Math.max(1, ...months.map((m) => m.predictedRequests)),
  };
}
